import { useEffect } from 'react';
import { site } from '@/config/site';

export interface DocumentMeta {
  /** Page title, without the brand suffix - that is appended here. */
  readonly title: string;
  /** Meta description. Falls back to the site-wide description. */
  readonly description?: string;
  /** Path of the canonical URL, e.g. `/services`. */
  readonly path: string;
}

/**
 * Sets the document title, description, canonical URL and Open Graph tags for a route.
 *
 * This is a client-side SPA, so crawlers that do not execute JavaScript see only the
 * static tags in `index.html`. Google renders JS and will pick these up; several social
 * crawlers will not. If organic search or link previews become commercially important,
 * the fix is prerendering at build time - see `docs/architecture.md`.
 */
export function useDocumentMeta({ title, description, path }: DocumentMeta): void {
  useEffect(() => {
    const fullTitle =
      title === site.name ? `${site.name} - ${site.tagline}` : `${title} - ${site.name}`;
    const desc = description ?? site.description;
    const canonical = new URL(path, site.url).toString();

    document.title = fullTitle;

    setMetaByName('description', desc);
    setMetaByProperty('og:title', fullTitle);
    setMetaByProperty('og:description', desc);
    setMetaByProperty('og:url', canonical);
    setMetaByProperty('og:type', 'website');
    setMetaByName('twitter:card', 'summary_large_image');
    setMetaByName('twitter:title', fullTitle);
    setMetaByName('twitter:description', desc);
    setCanonical(canonical);
  }, [title, description, path]);
}

function upsertMeta(selector: string, create: () => HTMLMetaElement, content: string): void {
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  const element = existing ?? document.head.appendChild(create());
  element.content = content;
}

function setMetaByName(name: string, content: string): void {
  upsertMeta(
    `meta[name="${name}"]`,
    () => {
      const el = document.createElement('meta');
      el.name = name;
      return el;
    },
    content
  );
}

function setMetaByProperty(property: string, content: string): void {
  upsertMeta(
    `meta[property="${property}"]`,
    () => {
      const el = document.createElement('meta');
      el.setAttribute('property', property);
      return el;
    },
    content
  );
}

function setCanonical(href: string): void {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  const element =
    existing ??
    document.head.appendChild(
      (() => {
        const el = document.createElement('link');
        el.rel = 'canonical';
        return el;
      })()
    );
  element.href = href;
}
